import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { X, Heart, ArrowRight } from 'lucide-react';

const PROMO_END = new Date('2026-04-30T23:59:59');
const STORAGE_KEY = 'ird_promo_dismissed';

function useCountdown(target) {
  const [left, setLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      setLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return left;
}

const PromoBanner = () => {
  const { language } = useLanguage();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const { days, hours, mins, secs } = useCountdown(PROMO_END);

  if (dismissed || Date.now() > PROMO_END) return null;

  const handleClose = () => {
    setDismissed(true);
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* sessionStorage unavailable in private mode */ }
  };

  const text = {
    it: { tag: 'Offerta Primavera', offer: '-15% su tutti i pacchetti Honeymoon in Italy', cta: 'Scopri Ora', d: 'g', h: 'h', m: 'm', s: 's' },
    en: { tag: 'Spring Offer', offer: '-15% on all Honeymoon in Italy packages', cta: 'Discover Now', d: 'd', h: 'h', m: 'm', s: 's' },
    es: { tag: 'Oferta de Primavera', offer: '-15% en todos los paquetes Honeymoon in Italy', cta: 'Descubre Ahora', d: 'd', h: 'h', m: 'm', s: 's' },
    pt: { tag: 'Oferta de Primavera', offer: '-15% em todos os pacotes Honeymoon in Italy', cta: 'Descubra Agora', d: 'd', h: 'h', m: 'm', s: 's' },
  }[language] || text?.en || { tag: 'Spring Offer', offer: '-15% on all Honeymoon in Italy packages', cta: 'Discover Now', d: 'd', h: 'h', m: 'm', s: 's' };

  const t = {
    it: { tag: 'Offerta Primavera', offer: '-15% su tutti i pacchetti Honeymoon in Italy', cta: 'Scopri Ora', d: 'g', h: 'h', m: 'm', s: 's' },
    en: { tag: 'Spring Offer', offer: '-15% on all Honeymoon in Italy packages', cta: 'Discover Now', d: 'd', h: 'h', m: 'm', s: 's' },
    es: { tag: 'Oferta de Primavera', offer: '-15% en todos los paquetes Honeymoon in Italy', cta: 'Descubre Ahora', d: 'd', h: 'h', m: 'm', s: 's' },
    pt: { tag: 'Oferta de Primavera', offer: '-15% em todos os pacotes Honeymoon in Italy', cta: 'Descubra Agora', d: 'd', h: 'h', m: 'm', s: 's' },
  }[language] || { tag: 'Spring Offer', offer: '-15% on all Honeymoon in Italy packages', cta: 'Discover Now', d: 'd', h: 'h', m: 'm', s: 's' };

  const Digit = ({ val, label }) => (
    <div className="flex flex-col items-center mx-1 sm:mx-1.5">
      <span className="text-white font-bold text-base sm:text-lg leading-none tabular-nums" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {String(val).padStart(2, '0')}
      </span>
      <span className="text-white/60 text-[10px] uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div
      className="relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #7f1d1d, #991b1b, #9f1239, #881337)' }}
      data-testid="promo-banner"
    >
      {/* Sparkle pattern */}
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '24px 24px' }} />

      <div className="relative max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 sm:gap-5 flex-wrap">
        {/* Tag */}
        <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <Heart className="w-3.5 h-3.5 text-rose-300" />
          <span className="text-white text-xs font-bold uppercase tracking-wider">{t.tag}</span>
        </div>

        {/* Offer text */}
        <span className="text-white text-sm sm:text-base font-semibold text-center">
          {t.offer}
        </span>

        {/* Countdown */}
        <div className="flex items-center bg-black/20 rounded-lg px-3 py-1">
          <Digit val={days} label={t.d} />
          <span className="text-white/40 text-sm mx-0.5">:</span>
          <Digit val={hours} label={t.h} />
          <span className="text-white/40 text-sm mx-0.5">:</span>
          <Digit val={mins} label={t.m} />
          <span className="text-white/40 text-sm mx-0.5">:</span>
          <Digit val={secs} label={t.s} />
        </div>

        {/* CTA */}
        <Link
          to="/spring-offer"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 hover:scale-105"
          style={{ background: 'rgba(255,255,255,0.95)', color: '#7f1d1d' }}
          data-testid="promo-cta-btn"
        >
          {t.cta}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>

        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/10 transition"
          data-testid="promo-close-btn"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-white/60" />
        </button>
      </div>
    </div>
  );
};

export default PromoBanner;
